import { Role } from "../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { SplatProspectResponse } from "providers/Types";

const monthKeys = [
  "janUsage",
  "febUsage",
  "marUsage",
  "aprUsage",
  "mayUsage",
  "junUsage",
  "julUsage",
  "augUsage",
  "sepUsage",
  "octUsage",
  "novUsage",
  "decUsage",
];

type Month = { month: string; days: number };

const monthsAndDays: Month[] = [
  { month: "Jan", days: 31 },
  { month: "Feb", days: 28 },
  { month: "Mar", days: 31 },
  { month: "Apr", days: 30 },
  { month: "May", days: 31 },
  { month: "Jun", days: 30 },
  { month: "Jul", days: 31 },
  { month: "Aug", days: 31 },
  { month: "Sep", days: 30 },
  { month: "Oct", days: 31 },
  { month: "Nov", days: 30 },
  { month: "Dec", days: 31 },
];

const dailyCompanies = [
  "SCE",
  "SVP",
  "PSE&G",
  "ConEd",
  "PSEGLI",
  "Silicon Valley Power",
  "ConEdison",
];

const isEmptyValue = (value: string | undefined | null) => {
  if (!value || value === "") {
    return true;
  }
  return false;
};

const formatDataForMonthly = (
  usageData: (number | undefined)[],
  months: Month[],
  usageCadence?: "monthly" | "daily"
): { month: string; value: number }[] =>
  usageData.map((val: number | undefined, index: number) => {
    const numValue = val ?? 0;
    return {
      month: months[index].month,
      value:
        usageCadence === "monthly" ? numValue : numValue * months[index].days,
    };
  });

const determineUpdatedValue = (
  val1: string | undefined | number | boolean,
  val2: string | undefined | number | boolean
): boolean => {
  if (typeof val1 === "boolean" || typeof val2 === "boolean") {
    return false;
  }
  if (val1 && val2) {
    return val1 !== val2;
  }
  const emptyArr: (string | undefined | number)[] = ["", undefined];

  if (emptyArr.includes(val1) && !emptyArr.includes(val2)) {
    return true;
  } else if (!emptyArr.includes(val1) && emptyArr.includes(val2)) {
    return true;
  }
  return false;
};

const PROPOSAL_VOIDING_FORM_FIELDS = [
  "customerState",
  "customerZipCode",
  "customerStreet",
  "customerCity",
  "customerFirstName",
  "customerLastName",
  "utilityCompany",
  "rateSchedule",
  ...monthKeys,
];

const defaultAllowedFileTypes = [
  "application/msword",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "image/tiff",
];

const imageFileTypes = ["jpeg", "jpg", "png", "tiff"];

type VoidingField = (typeof PROPOSAL_VOIDING_FORM_FIELDS)[number];

type UtilityInfoState = {
  utilityBillDate?: string;
  utilityCompany?: string;
  utilityServiceAddress?: string;
  rateSchedule?: string;
  accountNumber?: string;
  meterNumber?: string;
};

interface Attachment {
  documentName: string;
  documentType: string;
  contentType: string;
  url: string;
  documentBlob: Blob;
  documentId?: string;
  new?: boolean;
}

type UsageCadence = "monthly" | "daily";

const ROLES: Record<string, Role> = {
  UTILITYBILLCONTACT: "UtilityBillContact",
  HOMEOWNER: "Homeowner",
  CONTRACTSIGNER: "Contract Signer",
  CONTRACTCOSIGNER: "Contract Cosigner",
  OTHER: "Other",
  CREDIT: "Credit",
  BILLING: "Billing",
  DEPOSIT: "Deposit",
  ACH: "ACH",
  HOMEBUILDER: "Homebuilder",
  CREDITCONTACT: "Credit Contact",
};

const getMonthProperty = (obj: any) => {
  const months = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
  ];

  return months.map((month) => (obj ? obj[month as keyof typeof obj] || 0 : 0));
};

type UsageCalculationResult = {
  totalUsage: number;
  monthsEntered: number;
  monthlyUsage: (number | undefined)[];
};

const getMonthlyUsage = (prospect: SplatProspectResponse) =>
  monthKeys.map(
    (key) => prospect[key as keyof SplatProspectResponse] as number | undefined
  );
/**
 * Calculates usage data from prospect information
 * @param prospect - The prospect response containing monthly usage data
 * @returns Object containing total usage, months entered, and estimated annual usage
 */
const calculateUsageData = ({
  prospect,
  usageData,
  daily,
}: {
  prospect?: SplatProspectResponse;
  usageData?: (number | undefined)[];
  daily?: boolean;
}): UsageCalculationResult => {
  if (usageData) {
    return {
      totalUsage: usageData.reduce(
        (sum: number, val: number | undefined, index: number) =>
          sum + (val || 0) * (daily ? monthsAndDays[index].days : 1),
        0
      ),
      monthsEntered: usageData.filter(
        (val) => val !== undefined && val !== null
      ).length,
      monthlyUsage: usageData,
    };
  }

  if (!prospect)
    return {
      totalUsage: 0,
      monthsEntered: 0,
      monthlyUsage: new Array(12).fill(undefined),
    };

  const monthlyUsage = getMonthlyUsage(prospect);

  const totalUsage = monthlyUsage.reduce(
    (sum: number, val: number | undefined) => sum + (val || 0),
    0
  );
  const monthsEntered = monthlyUsage.filter(
    (val) => val !== undefined && val !== null
  ).length;

  return { totalUsage, monthsEntered, monthlyUsage };
};

export type {
  Attachment,
  UsageCadence,
  UtilityInfoState,
  VoidingField,
  UsageCalculationResult,
};
export {
  calculateUsageData,
  defaultAllowedFileTypes,
  determineUpdatedValue,
  formatDataForMonthly,
  getMonthlyUsage,
  getMonthProperty,
  imageFileTypes,
  isEmptyValue,
  monthKeys,
  monthsAndDays,
  PROPOSAL_VOIDING_FORM_FIELDS,
  ROLES,
  dailyCompanies,
};
